<template>
  <multiselect
    :model-value="selectedOption"
    :options="options"
    label="city_name"
    :track-by="valueField"
    :placeholder="placeholder"
    :searchable="true"
    :internal-search="true"
    :multiple="multiple"
    :close-on-select="!multiple"
    :clear-on-select="false"
    :allow-empty="multiple || allowEmpty"
    select-label="Select"
    deselect-label="Remove"
    :loading="loading"
    :disabled="disabled"
    :class="{ 'is-invalid': invalid }"
    @update:model-value="onSelect"
    @search-change="onSearch"
    @open="onOpen"
  >
    <template #afterList>
      <li v-if="hasMore" :ref="setSentinel" class="multiselect__element">
        <span class="multiselect__option text-center text-muted">
          Loading more...
        </span>
      </li>
    </template>
  </multiselect>
</template>

<script>
// City dropdown backed by the paginated `/city-paginated` API: the first page
// loads on open, the next page loads as the list is scrolled to its end, and
// typing searches on the server. The model value is the city id.
import Multiselect from "vue-multiselect";
import "vue-multiselect/dist/vue-multiselect.css";
import CityService from "@/services/CityService";

export default {
  name: "CitySelect",

  components: { Multiselect },

  props: {
    // Single mode: depends on `shape`. Multiple mode: an array of city objects.
    modelValue: { type: [String, Number, Array, Object], default: "" },
    // What v-model holds in single mode:
    //   "id"          the city id (default)
    //   "option"      the city object as the API returns it ({ id, city_name, ... })
    //   "value-label" a { value, label } object, like CustomSelect2 uses
    shape: { type: String, default: "id" },
    multiple: { type: Boolean, default: false },
    // Which city field is used as the value: "id", or "city_name" for the
    // filters that match on the city name.
    valueField: { type: String, default: "id" },
    placeholder: { type: String, default: "Search and select city" },
    // Optional scope: only cities of this state / country (sent to /city-paginated).
    stateId: { type: [String, Number], default: "" },
    countryId: { type: [String, Number], default: "" },
    invalid: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    allowEmpty: { type: Boolean, default: true },
    perPage: { type: Number, default: 20 },
  },

  emits: ["update:modelValue", "change"],

  data() {
    return {
      options: [],
      // Saved city that isn't in the loaded pages yet (e.g. edit forms).
      fetchedSelected: null,
      page: 0,
      totalPages: 1,
      search: "",
      loading: false,
      initialized: false,
      requestId: 0,
      scopedCities: null,
      scopedKey: "",
      searchTimer: null,
      sentinel: null,
      observer: null,
    };
  },

  computed: {
    hasMore() {
      return this.initialized && this.page < this.totalPages;
    },
    hasValue() {
      if (this.multiple) return false;
      return this.modelValue !== "" && this.modelValue !== null && this.modelValue !== undefined;
    },
    selectedOption() {
      if (this.multiple) return Array.isArray(this.modelValue) ? this.modelValue : [];
      if (!this.hasValue) return null;
      // Object shapes already carry the label, so nothing has to be looked up.
      if (this.shape === "option") {
        return this.modelValue && this.modelValue.id != null ? this.modelValue : null;
      }
      if (this.shape === "value-label") {
        const v = this.modelValue;
        if (!(v && v.value != null && v.value !== "")) return null;
        const fetched =
          this.fetchedSelected && String(this.fetchedSelected.id) === String(v.value)
            ? this.fetchedSelected.city_name
            : "";
        return { id: v.value, city_name: v.label || fetched };
      }
      const key = this.valueField;
      const value = String(this.modelValue);
      const found =
        this.options.find((el) => String(el[key]) === value) ||
        (this.fetchedSelected && String(this.fetchedSelected[key]) === value
          ? this.fetchedSelected
          : null);
      if (found || key === "id") return found;
      // Value is a plain name that isn't in the loaded pages: show it as is.
      return { [key]: this.modelValue, city_name: this.modelValue };
    },
  },

  watch: {
    // The state / country changed: the loaded pages belong to the old scope.
    stateId() {
      this.resetOptions();
    },
    countryId() {
      this.resetOptions();
    },
    modelValue: {
      immediate: true,
      handler() {
        this.resolveSelected();
      },
    },
  },

  mounted() {
    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.loadMore();
      },
      { threshold: 0 }
    );
    if (this.sentinel) this.observer.observe(this.sentinel);
  },

  beforeUnmount() {
    clearTimeout(this.searchTimer);
    this.observer?.disconnect();
  },

  methods: {
    setSentinel(el) {
      if (this.sentinel && this.sentinel !== el) {
        this.observer?.unobserve(this.sentinel);
      }
      this.sentinel = el;
      if (el) this.observer?.observe(el);
    },
    async resolveSelected() {
      const v = this.modelValue;
      if (this.shape === "value-label") {
        // { value, label } without a label: look the name up.
        if (!(v && v.value != null && v.value !== "") || v.label || this.selectedOption?.city_name) return;
      } else if (this.shape !== "id" || this.valueField !== "id" || !this.hasValue || this.selectedOption) {
        return;
      }
      try {
        const response = await CityService.getCityById(this.shape === "value-label" ? v.value : v);
        this.fetchedSelected = response.data;
      } catch (error) {
        console.error("Error fetching city:", error);
      }
    },
    // Cities of the current state, fetched once per state / country.
    async loadScopedCities() {
      const key = `${this.stateId}-${this.countryId}`;
      if (this.scopedKey !== key) {
        this.scopedKey = key;
        this.scopedCities = null;
      }
      if (!this.scopedCities) {
        try {
          const res = await CityService.getStateCities(this.stateId, this.countryId);
          this.scopedCities = res?.data || [];
        } catch (error) {
          // "City does not exist" (no cities for this state) comes back as an error.
          this.scopedCities = [];
        }
      }
      return this.scopedCities;
    },
    resetOptions() {
      clearTimeout(this.searchTimer);
      this.requestId++; // drop any request still in flight
      this.options = [];
      this.page = 0;
      this.totalPages = 1;
      this.search = "";
      this.loading = false;
      this.initialized = false;
    },
    onOpen() {
      if (!this.initialized) this.fetchPage(1);
    },
    onSearch(query) {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        if (query === this.search) return;
        this.search = query;
        this.fetchPage(1);
      }, 300);
    },
    loadMore() {
      if (this.loading || !this.hasMore) return;
      this.fetchPage(this.page + 1);
    },
    async fetchPage(page) {
      const requestId = ++this.requestId;
      this.loading = true;
      try {
        let data;
        let totalPages;
        if (this.stateId && this.countryId) {
          // Scoped to a state: that list is small, so use the existing
          // /city-get/{state}/{country} endpoint and page / search it here.
          const all = await this.loadScopedCities();
          if (requestId !== this.requestId) return;
          const q = this.search.trim().toLowerCase();
          const matching = q
            ? all.filter((c) => String(c.city_name || "").toLowerCase().includes(q))
            : all;
          data = matching.slice((page - 1) * this.perPage, page * this.perPage);
          totalPages = Math.max(1, Math.ceil(matching.length / this.perPage));
        } else {
          // Only page / per_page are guaranteed by the API. `search` is passed along
          // in case the server filters by it; the list is also filtered here, so
          // typing works either way (the sentinel keeps loading pages while the
          // filtered list is still short).
          const params = { page, per_page: this.perPage };
          if (this.search) params.search = this.search;
          const response = await CityService.getCitiesPaginated(params);
          if (requestId !== this.requestId) return;
          data = response.data.data;
          totalPages = response.data.pagination?.total_pages || 1;
        }
        this.options = page === 1 ? data : [...this.options, ...data];
        this.page = page;
        this.totalPages = totalPages;
        this.initialized = true;
      } catch (error) {
        if (requestId === this.requestId) {
          console.error("Error fetching cities:", error);
        }
      } finally {
        if (requestId === this.requestId) {
          this.loading = false;
          // Re-arm the observer: it only reports changes, and the sentinel
          // may still be on screen after a short page.
          this.$nextTick(() => {
            if (this.sentinel && this.observer) {
              this.observer.unobserve(this.sentinel);
              this.observer.observe(this.sentinel);
            }
          });
        }
      }
    },
    onSelect(option) {
      if (this.multiple) {
        this.$emit("update:modelValue", option || []);
        this.$emit("change", option || []);
        return;
      }
      this.fetchedSelected = option || null;
      let value;
      if (this.shape === "option") value = option || null;
      else if (this.shape === "value-label") {
        value = option ? { value: option.id, label: option.city_name } : null;
      } else value = option ? option[this.valueField] : "";
      this.$emit("update:modelValue", value);
      this.$emit("change", option || null);
    },
  },
};
</script>
